import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslations } from './hooks/useTranslations';

const Navigation = () => {
  const { t } = useTranslations();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-sm border-b border-white/10 w-full">
      <div className="w-full px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 w-full">
          <Link to="/" className="text-lg sm:text-xl lg:text-2xl font-bold text-white flex-shrink-0" onClick={closeMobileMenu}>
            Your<span className="text-purple-400">Brand</span>Value
          </Link>
          
          <div className="hidden md:flex space-x-4 lg:space-x-8 flex-shrink-0">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.home')}</Link>
            <Link to="/sobre-mi" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.aboutMe')}</Link>
            <Link to="/metodologia" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.methodology')}</Link>
            <Link to="/casos-estudio" className="text-purple-400 hover:text-purple-300 transition-colors text-sm lg:text-base font-semibold">{t('navigation.caseStudies')}</Link>
            <Link to="/asesoria" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.consulting')}</Link>
            <Link to="/contacto" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.contact')}</Link>
          </div>

          <div className="md:hidden flex-shrink-0">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-300 hover:text-white p-2 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-800/95 backdrop-blur-sm border-t border-white/10">
            <div className="px-4 pt-2 pb-3 space-y-1">
              <Link to="/" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.home')}</Link>
              <Link to="/sobre-mi" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.aboutMe')}</Link>
              <Link to="/metodologia" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.methodology')}</Link>
              <Link to="/casos-estudio" className="block text-purple-400 hover:text-purple-300 py-2 transition-colors font-semibold" onClick={closeMobileMenu}>{t('navigation.caseStudies')}</Link>
              <Link to="/asesoria" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.consulting')}</Link>
              <Link to="/contacto" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.contact')}</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const CasosEstudioPage = () => {
  const { t } = useTranslations();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get translated case studies from i18n
  const caseStudies = [
    {
      id: 'ecommerce',
      title: t('caseStudies.cases.ecommerce.title'),
      sector: t('caseStudies.cases.ecommerce.sector'),
      challenge: t('caseStudies.cases.ecommerce.challenge'),
      solution: t('caseStudies.cases.ecommerce.solution'),
      context: t('caseStudies.cases.ecommerce.context'),
      duration: t('caseStudies.cases.ecommerce.duration'),
      icon: t('caseStudies.cases.ecommerce.icon'),
      color: t('caseStudies.cases.ecommerce.color'),
      borderColor: t('caseStudies.cases.ecommerce.borderColor'),
      results: [
        { metric: t('caseStudies.labels.countries'), value: t('caseStudies.cases.ecommerce.results.countries.value'), description: t('caseStudies.cases.ecommerce.results.countries.description') },
        { metric: t('caseStudies.labels.references'), value: t('caseStudies.cases.ecommerce.results.references.value'), description: t('caseStudies.cases.ecommerce.results.references.description') },
        { metric: t('caseStudies.labels.innovation'), value: t('caseStudies.cases.ecommerce.results.innovation.value'), description: t('caseStudies.cases.ecommerce.results.innovation.description') },
        { metric: t('caseStudies.labels.model'), value: t('caseStudies.cases.ecommerce.results.model.value'), description: t('caseStudies.cases.ecommerce.results.model.description') }
      ]
    },
    {
      id: 'pandemia',
      title: t('caseStudies.cases.pandemia.title'),
      sector: t('caseStudies.cases.pandemia.sector'),
      challenge: t('caseStudies.cases.pandemia.challenge'),
      solution: t('caseStudies.cases.pandemia.solution'),
      context: t('caseStudies.cases.pandemia.context'),
      duration: t('caseStudies.cases.pandemia.duration'),
      icon: t('caseStudies.cases.pandemia.icon'),
      color: t('caseStudies.cases.pandemia.color'),
      borderColor: t('caseStudies.cases.pandemia.borderColor'),
      results: [
        { metric: t('caseStudies.labels.revenue'), value: t('caseStudies.cases.pandemia.results.revenue.value'), description: t('caseStudies.cases.pandemia.results.revenue.description') },
        { metric: t('caseStudies.labels.conversion'), value: t('caseStudies.cases.pandemia.results.conversion.value'), description: t('caseStudies.cases.pandemia.results.conversion.description') },
        { metric: t('caseStudies.labels.socialImpact'), value: t('caseStudies.cases.pandemia.results.socialImpact.value'), description: t('caseStudies.cases.pandemia.results.socialImpact.description') },
        { metric: t('caseStudies.labels.avgTicket'), value: t('caseStudies.cases.pandemia.results.avgTicket.value'), description: t('caseStudies.cases.pandemia.results.avgTicket.description') }
      ]
    },
    {
      id: 'captio',
      title: t('caseStudies.cases.captio.title'),
      sector: t('caseStudies.cases.captio.sector'),
      challenge: t('caseStudies.cases.captio.challenge'),
      solution: t('caseStudies.cases.captio.solution'),
      context: t('caseStudies.cases.captio.context'),
      duration: t('caseStudies.cases.captio.duration'),
      icon: t('caseStudies.cases.captio.icon'),
      color: t('caseStudies.cases.captio.color'),
      borderColor: t('caseStudies.cases.captio.borderColor'),
      results: [
        { metric: t('caseStudies.labels.markets'), value: t('caseStudies.cases.captio.results.markets.value'), description: t('caseStudies.cases.captio.results.markets.description') },
        { metric: t('caseStudies.labels.ecosystem'), value: t('caseStudies.cases.captio.results.ecosystem.value'), description: t('caseStudies.cases.captio.results.ecosystem.description') },
        { metric: t('caseStudies.labels.growth'), value: t('caseStudies.cases.captio.results.growth.value'), description: t('caseStudies.cases.captio.results.growth.description') },
        { metric: t('caseStudies.labels.exit'), value: t('caseStudies.cases.captio.results.exit.value'), description: t('caseStudies.cases.captio.results.exit.description') }
      ]
    }
  ];

  const testimonialHighlights = [
    {
      id: 'startup',
      quote: t('caseStudies.testimonialsList.startup.quote'),
      author: t('caseStudies.testimonialsList.startup.author'),
      context: t('caseStudies.testimonialsList.startup.context'),
      metric: t('caseStudies.testimonialsList.startup.metric'),
      icon: t('caseStudies.testimonialsList.startup.icon')
    },
    {
      id: 'ecommerce',
      quote: t('caseStudies.testimonialsList.ecommerce.quote'),
      author: t('caseStudies.testimonialsList.ecommerce.author'),
      context: t('caseStudies.testimonialsList.ecommerce.context'),
      metric: t('caseStudies.testimonialsList.ecommerce.metric'),
      icon: t('caseStudies.testimonialsList.ecommerce.icon')
    },
    {
      id: 'consulting',
      quote: t('caseStudies.testimonialsList.consulting.quote'),
      author: t('caseStudies.testimonialsList.consulting.author'),
      context: t('caseStudies.testimonialsList.consulting.context'),
      metric: t('caseStudies.testimonialsList.consulting.metric'),
      icon: t('caseStudies.testimonialsList.consulting.icon')
    }
  ];

  const industryStats = [
    { 
      sector: t('caseStudies.industryStats.ecommerce.sector'), 
      projects: t('caseStudies.industryStats.ecommerce.projects'), 
      avgGrowth: t('caseStudies.industryStats.ecommerce.avgGrowth'), 
      timeframe: t('caseStudies.industryStats.ecommerce.timeframe'), 
      context: t('caseStudies.industryStats.ecommerce.context')
    },
    { 
      sector: t('caseStudies.industryStats.saas.sector'), 
      projects: t('caseStudies.industryStats.saas.projects'), 
      avgGrowth: t('caseStudies.industryStats.saas.avgGrowth'), 
      timeframe: t('caseStudies.industryStats.saas.timeframe'), 
      context: t('caseStudies.industryStats.saas.context')
    },
    { 
      sector: t('caseStudies.industryStats.consulting.sector'), 
      projects: t('caseStudies.industryStats.consulting.projects'), 
      avgGrowth: t('caseStudies.industryStats.consulting.avgGrowth'), 
      timeframe: t('caseStudies.industryStats.consulting.timeframe'), 
      context: t('caseStudies.industryStats.consulting.context')
    },
    { 
      sector: t('caseStudies.industryStats.retail.sector'), 
      projects: t('caseStudies.industryStats.retail.projects'), 
      avgGrowth: t('caseStudies.industryStats.retail.avgGrowth'), 
      timeframe: t('caseStudies.industryStats.retail.timeframe'), 
      context: t('caseStudies.industryStats.retail.context')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      
      {/* Header */}
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
          >
            <span className="mr-2">←</span>
            {t('home.calculator.backHome')}
          </Link>

          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-white mb-6">
              {t('caseStudies.hero.title')}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('caseStudies.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Casos de Estudio Principales */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('caseStudies.featured.title')}</h2>
            <p className="text-xl text-gray-300">
              {t('caseStudies.featured.subtitle')}
            </p>
          </div>

          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className={`bg-gradient-to-br ${study.color} backdrop-blur-sm rounded-2xl border ${study.borderColor} p-8`}>
                    <div className="flex items-center mb-6">
                      <div className="text-4xl mr-4">{study.icon}</div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-1">{study.title}</h3>
                        <p className="text-purple-300 text-sm">{study.sector}</p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">🎯 {t('caseStudies.labels.context')}</h4>
                        <p className="text-gray-300 text-sm leading-relaxed">{study.challenge}</p>
                      </div>

                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">💡 {t('caseStudies.labels.approach')}</h4>
                        <p className="text-gray-300 text-sm leading-relaxed">{study.solution}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">🔧 {t('caseStudies.labels.workDone')}</h4>
                        <p className="text-gray-400 text-sm leading-relaxed italic">{study.context}</p>
                      </div>

                      <div className="flex items-center text-sm text-gray-400">
                        <span className="mr-2">⏱️</span>
                        <span>{t('caseStudies.labels.duration')}: {study.duration}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8">
                    <h4 className="text-2xl font-bold text-white mb-8 text-center">📈 {t('caseStudies.labels.results')}</h4>
                    
                    <div className="grid grid-cols-2 gap-6">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="text-center">
                          <div className="text-3xl font-bold text-green-400 mb-2">{result.value}</div>
                          <div className="text-white font-semibold text-sm mb-1">{result.metric}</div>
                          <div className="text-gray-400 text-xs">{result.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonios */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('caseStudies.testimonials.title')}</h2>
            <p className="text-xl text-gray-300">
              {t('caseStudies.testimonials.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonialHighlights.map((testimonial, index) => (
              <div
                key={index}
                className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8 hover:border-purple-500/40 transition-all duration-300"
              >
                <div className="text-center mb-6">
                  <div className="text-4xl mb-4">{testimonial.icon}</div>
                  <blockquote className="text-gray-300 italic mb-4 leading-relaxed">
                    "{testimonial.quote}"
                  </blockquote>
                  <div className="text-white font-semibold">{testimonial.author}</div>
                  <div className="text-purple-300 text-sm">{testimonial.context}</div>
                </div>
                
                <div className="text-center">
                  <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-full px-4 py-2 inline-block">
                    <span className="text-green-400 font-bold text-sm">{testimonial.metric}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Estadísticas por Industria */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('caseStudies.industryResults.title')}</h2>
            <p className="text-xl text-gray-300">
              {t('caseStudies.industryResults.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industryStats.map((stat, index) => (
              <div
                key={index}
                className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-6 text-center hover:border-purple-500/40 transition-all duration-300"
              >
                <h3 className="text-xl font-bold text-white mb-4">{stat.sector}</h3>
                
                <div className="space-y-4">
                  <div>
                    <div className="text-2xl font-bold text-purple-400">{stat.projects}</div>
                    <div className="text-gray-400 text-sm">{t('caseStudies.labels.projects')}</div>
                  </div>
                  
                  <div>
                    <div className="text-2xl font-bold text-green-400">{stat.avgGrowth}</div>
                    <div className="text-gray-400 text-sm">{t('caseStudies.labels.avgGrowth')}</div>
                  </div>
                  
                  <div>
                    <div className="text-lg font-semibold text-orange-400">{stat.timeframe}</div>
                    <div className="text-gray-400 text-sm">{t('caseStudies.labels.timeToResults')}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary Stats */}
          <div className="mt-16 bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-8">{t('caseStudies.summary.title')}</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-purple-400 mb-2">{t('caseStudies.summaryStats.projectsCompleted')}</div>
                  <div className="text-sm text-gray-300">{t('caseStudies.labels.projectsCompleted')}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-400 mb-2">{t('caseStudies.summaryStats.averageGrowth')}</div>
                  <div className="text-sm text-gray-300">{t('caseStudies.labels.averageGrowth')}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-400 mb-2">{t('caseStudies.summaryStats.averageTime')}</div>
                  <div className="text-sm text-gray-300">{t('caseStudies.labels.averageTime')}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">{t('caseStudies.summaryStats.clientSatisfaction')}</div>
                  <div className="text-sm text-gray-300">{t('caseStudies.labels.clientSatisfaction')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-12">
            <h2 className="text-3xl font-bold text-white mb-4">{t('caseStudies.cta.title')}</h2>
            <p className="text-xl text-gray-300 mb-8">
              {t('caseStudies.cta.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/asesoria"
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                {t('caseStudies.cta.bookSession')}
              </Link>
              <Link
                to="/calculator"
                className="border-2 border-purple-500 text-purple-400 px-8 py-4 rounded-xl font-bold text-lg hover:bg-purple-500 hover:text-white transition-all duration-300"
              >
                {t('caseStudies.cta.learnMore')}
              </Link>
            </div>
            
            <p className="text-sm text-gray-400 mt-4">
              ✅ {t('caseStudies.benefits.freeConsultation')} • ✅ {t('caseStudies.benefits.provenMethodology')} • ✅ {t('caseStudies.benefits.measurableResults')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CasosEstudioPage;